/**
 * Modern Button Component
 * 
 * A reusable, accessible button component with vibrant gradients and excellent dark mode support.
 * 
 * @example
 * // Primary button (default)
 * <Button>Submit</Button>
 * 
 * @example
 * // Secondary button
 * <Button variant="secondary">Cancel</Button>
 * 
 * @example
 * // Ghost button for toolbar actions
 * <Button variant="ghost" size="icon">
 *   <Icon />
 * </Button>
 * 
 * @example
 * // Danger button for destructive actions
 * <Button variant="danger">Delete</Button>
 * 
 * @example
 * // Different sizes
 * <Button size="sm">Small</Button>
 * <Button size="md">Medium</Button>
 * <Button size="lg">Large</Button>
 * 
 * Variants:
 * - primary: Vibrant indigo→violet gradient (default)
 * - secondary: Clean outline with hover effects
 * - ghost: Transparent with subtle hover
 * - danger: Red gradient for destructive actions
 * 
 * Legacy variants (backward compatible):
 * - default → primary
 * - outline → secondary
 * - destructive → danger
 */
import React from 'react';
import { cn } from '../../lib/utils';

const Button = React.forwardRef(({ 
  className, 
  variant = 'primary', 
  size = 'md', 
  children, 
  ...props 
}, ref) => {
  // Base styles - structural only (no background/border/color)
  const baseStyles = [
    'inline-flex items-center justify-center',
    'font-semibold',
    'transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:opacity-50 disabled:pointer-events-none disabled:cursor-not-allowed',
    'active:scale-[0.98]',
    'relative overflow-hidden',
  ].join(' ');

  // Variant styles with enhanced dark mode support
  const variants = {
    // Primary: Vibrant gradient (indigo → violet) with excellent dark mode contrast
    primary: [
      'bg-gradient-to-r from-indigo-600 via-indigo-600 to-violet-600',
      'dark:from-indigo-500 dark:via-indigo-500 dark:to-violet-500',
      'text-white',
      'shadow-md shadow-indigo-500/30 dark:shadow-indigo-500/50',
      'hover:from-indigo-700 hover:via-indigo-600 hover:to-violet-700',
      'dark:hover:from-indigo-400 dark:hover:via-indigo-400 dark:hover:to-violet-400',
      'hover:shadow-lg hover:shadow-indigo-500/40 dark:hover:shadow-indigo-500/60',
      'hover:scale-[1.02]',
      'focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400',
      'border border-transparent',
    ].join(' '),

    // Secondary: Clean outline with subtle background on hover
    secondary: [
      'border-2',
      'border-gray-300 dark:border-slate-600',
      'bg-white dark:bg-slate-800',
      'text-gray-700 dark:text-slate-200',
      'hover:border-indigo-500 dark:hover:border-indigo-400',
      'hover:bg-indigo-50 dark:hover:bg-indigo-950/30',
      'hover:text-indigo-700 dark:hover:text-indigo-300',
      'shadow-sm dark:shadow-slate-900/20',
      'hover:shadow-md hover:shadow-indigo-500/10 dark:hover:shadow-indigo-500/20',
      'hover:scale-[1.01]',
      'focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400',
    ].join(' '),

    // Ghost: Transparent with subtle hover glow
    ghost: [
      'border border-transparent',
      'text-gray-700 dark:text-slate-300',
      'hover:bg-gray-100 dark:hover:bg-slate-800',
      'hover:text-indigo-600 dark:hover:text-indigo-400',
      'focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400',
      'focus-visible:bg-gray-100 dark:focus-visible:bg-slate-800',
    ].join(' '),

    // Danger: Red gradient for destructive actions
    danger: [
      'bg-gradient-to-r from-red-600 via-red-600 to-rose-600',
      'dark:from-red-500 dark:via-red-500 dark:to-rose-500',
      'text-white',
      'shadow-md shadow-red-500/30 dark:shadow-red-500/50',
      'hover:from-red-700 hover:via-red-600 hover:to-rose-700',
      'dark:hover:from-red-400 dark:hover:via-red-400 dark:hover:to-rose-400',
      'hover:shadow-lg hover:shadow-red-500/40 dark:hover:shadow-red-500/60',
      'hover:scale-[1.02]',
      'focus-visible:ring-red-500 dark:focus-visible:ring-red-400',
      'border border-transparent',
    ].join(' '),

    // Legacy 'default' variant maps to 'primary' for backward compatibility
    default: [
      'bg-gradient-to-r from-indigo-600 via-indigo-600 to-violet-600',
      'dark:from-indigo-500 dark:via-indigo-500 dark:to-violet-500',
      'text-white',
      'shadow-md shadow-indigo-500/30 dark:shadow-indigo-500/50',
      'hover:from-indigo-700 hover:via-indigo-600 hover:to-violet-700',
      'dark:hover:from-indigo-400 dark:hover:via-indigo-400 dark:hover:to-violet-400',
      'hover:shadow-lg hover:shadow-indigo-500/40 dark:hover:shadow-indigo-500/60',
      'hover:scale-[1.02]',
      'focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400',
      'border border-transparent',
    ].join(' '),

    // Legacy 'outline' variant maps to 'secondary' for backward compatibility
    outline: [
      'border-2',
      'border-gray-300 dark:border-slate-600',
      'bg-white dark:bg-slate-800',
      'text-gray-700 dark:text-slate-200',
      'hover:border-indigo-500 dark:hover:border-indigo-400',
      'hover:bg-indigo-50 dark:hover:bg-indigo-950/30',
      'hover:text-indigo-700 dark:hover:text-indigo-300',
      'shadow-sm dark:shadow-slate-900/20',
      'hover:shadow-md hover:shadow-indigo-500/10 dark:hover:shadow-indigo-500/20',
      'hover:scale-[1.01]',
      'focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400',
    ].join(' '),

    // Legacy 'destructive' variant maps to 'danger' for backward compatibility
    destructive: [
      'bg-gradient-to-r from-red-600 via-red-600 to-rose-600',
      'dark:from-red-500 dark:via-red-500 dark:to-rose-500',
      'text-white',
      'shadow-md shadow-red-500/30 dark:shadow-red-500/50',
      'hover:from-red-700 hover:via-red-600 hover:to-rose-700',
      'dark:hover:from-red-400 dark:hover:via-red-400 dark:hover:to-rose-400',
      'hover:shadow-lg hover:shadow-red-500/40 dark:hover:shadow-red-500/60',
      'hover:scale-[1.02]',
      'focus-visible:ring-red-500 dark:focus-visible:ring-red-400',
      'border border-transparent',
    ].join(' '),

    // Unstyled: no visual styles at all; rely 100% on className
    unstyled: '',
  };

  // Size styles
  const sizes = {
    sm: 'h-8 px-3 text-xs rounded-md',
    md: 'h-10 px-5 text-sm rounded-lg',
    lg: 'h-12 px-6 text-base rounded-xl',
    icon: 'h-10 w-10 p-0 rounded-lg',
  };

  return (
    <button
      className={cn(
        baseStyles,
        variants[variant] || variants.primary,
        sizes[size] || sizes.md,
        className
      )}
      ref={ref}
      {...props}
    >
      {/* Subtle shine effect on hover for gradient buttons */}
      {(variant === 'primary' || variant === 'default' || variant === 'danger' || variant === 'destructive') && (
        <span 
          className="absolute inset-0 opacity-0 hover:opacity-20 transition-opacity duration-300 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%)',
          }}
        />
      )}
      <span className="relative z-10 flex items-center justify-center gap-2">{children}</span>
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
